import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom'
import styled from 'styled-components';
import { BsSearch } from 'react-icons/bs'
import { useRouteMatch } from 'react-router-dom'
import { useIntl, FormattedMessage } from "react-intl";

import { Input, Link } from './StyledComponents';
import LoginModal from './components/LoginModal'
import SignupModal from './components/SignupModal'
import ForgotPasswordModal from './components/ForgotPasswordModal'
import { loggedInContext } from './auth/AuthContext'
import type { User } from './auth/AuthContext'
import useWindowSize from './useWindowSize'
import useTenant from './client/useTenant'
import { useCsEvent } from './client/useEvents'
import HamburgerMenu from './components/HamburgerMenu';
import {
  useSearchSuggestions,
  SuggestionsDropdown,
  type SearchSuggestion,
} from './components/SearchAutocomplete';
import { getSearchPathPrefix } from './utils/searchPath';

const HeaderOuter = styled.header<{ $scrolled?: boolean }>`
  position: sticky;
  top: 0;
  z-index: 1000;
  background: ${({theme}) => theme.colors.headerBg};
  border-bottom: 1px solid ${({theme}) => theme.colors.headerBorder};
  box-shadow: ${({ theme, $scrolled }) =>
    $scrolled
      ? (theme.shadows?.elevated ?? '0 4px 16px rgba(0, 0, 0, 0.12)')
      : theme.colors.headerShadow};
  transition: box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease;
`;

const HeaderInner = styled.div<{ $scrolled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 ${({ $scrolled }) => ($scrolled ? '16px' : '12px')};
  height: ${({ $scrolled }) => ($scrolled ? '52px' : '56px')};
  transition: height 0.2s ease, padding 0.2s ease;

  @media (min-width: 576px) {
    gap: 16px;
    height: ${({ $scrolled }) => ($scrolled ? '52px' : '64px')};
    padding: 0 ${({ $scrolled }) => ($scrolled ? '16px' : '24px')};
  }
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  color: ${({theme}) => theme.colors.primary};
`;

const LogoImg = styled.img`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  transition: transform 0.2s ease;

  ${LogoLink}:hover & {
    transform: scale(1.05);
  }

  @media (min-width: 576px) {
    width: 36px;
    height: 36px;
  }
`;

const LogoText = styled.span`
  font-weight: 700;
  font-size: 1.1rem;
  color: ${({theme}) => theme.colors.primary};
  white-space: nowrap;

  @media (max-width: 420px) {
    display: none;
  }
`;

const SearchArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
  margin: 0;
  position: relative;
  z-index: 100;

  @media (min-width: 576px) {
    margin: 0 8px;
  }
`;

const SearchForm = styled.form`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 600px;
  background: ${({theme}) => theme.colors.searchBg};
  border: 1.5px solid ${({theme}) => theme.colors.searchBorder};
  border-radius: 50rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  &:focus-within {
    border-color: ${({theme}) => theme.colors.accent};
    box-shadow: 0 0 0 3px ${({theme}) => theme.mode === 'dark' ? 'rgba(255, 213, 79, 0.15)' : 'rgba(212, 168, 67, 0.12)'};
    background: ${({theme}) => theme.colors.searchFocusBg};
  }
`;

const SearchBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: none;
  border: none;
  color: ${({theme}) => theme.colors.accent};
  cursor: pointer;
  flex-shrink: 0;
  border-radius: 50rem 0 0 50rem;
  transition: color 0.2s;

  &:hover {
    color: ${({theme}) => theme.colors.accentHover};
  }

  @media (min-width: 576px) {
    width: 42px;
    height: 40px;
  }
`;

const SearchInput = styled(Input)`
  flex: 1;
  border: none;
  background: transparent;
  padding: 6px 2px;
  font-size: 0.9rem;
  outline: none;
  min-width: 0;
  color: ${({theme}) => theme.colors.text};

  &::placeholder {
    color: ${({theme}) => theme.colors.textMuted};
  }

  @media (min-width: 576px) {
    padding: 8px 4px;
    font-size: 0.95rem;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;


interface LoggedInContextValue {
  loggedIn: boolean;
  user: User | null;
  isLoginOpen: boolean;
  isSignupOpen: boolean;
  openLoginModal: (cta?: React.ReactNode) => void;
  openSignupModal: () => void;
  closeAllModals: () => void;
}

export function Header(_props: Record<string, never>) {
  const history = useHistory()
  const windowSize = useWindowSize()
  const intl = useIntl()
  const searchMatch = useRouteMatch<{ query: string }>("/ara/:query")
  const tagMatch = useRouteMatch<{ etiket: string }>("/etiket/:etiket")
  const {
    loggedIn,
    user,
    isLoginOpen,
    isSignupOpen,
    openLoginModal,
    openSignupModal,
    closeAllModals
  } = React.useContext(loggedInContext) as LoggedInContextValue
  const tenant = useTenant()
  const fireCsEvent = useCsEvent()

  const araInput = useRef<HTMLInputElement | null>(null)
  const matchQuery = searchMatch?.params?.query ?? tagMatch?.params?.etiket ?? ""
  const setAraInput = useCallback((node: HTMLInputElement | null) => {
    araInput.current = node
    if (node) {
      node.value = matchQuery
    }
  }, [matchQuery])
  const [showForgot, setShowForgot] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const ara = useCallback((e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!araInput.current?.value) return

    fireCsEvent({
      category: 'Arama',
      action: 'Submit',
      label: 'Kullanıcı arama yapıyor'
    })

    const url = new URL(`/${getSearchPathPrefix(tenant)}/${araInput.current.value}`,
      window.location.href)
    history.push({ pathname: url.pathname, search: url.search })
  }, [history, tenant, fireCsEvent])

  const handleSuggestionSelect = useCallback((suggestion: SearchSuggestion) => {
    fireCsEvent({ category: 'Arama', action: 'Suggestion', label: `Suggestion selected: ${suggestion.category}` })
    if (suggestion.category === 'newspapers') {
      history.push(`/gazete/${suggestion.value}`)
    } else if (suggestion.category === 'tags') {
      history.push(`/etiket/${suggestion.text}`)
    } else {
      if (araInput.current) araInput.current.value = suggestion.text
      history.push(`/${getSearchPathPrefix(tenant)}/${suggestion.text}`)
    }
  }, [history, tenant, fireCsEvent])

  const {
    suggestions: headerSuggestions,
    isOpen: suggestionsOpen,
    activeIndex,
    handleInput: handleSuggestInput,
    handleKeyDown: handleSuggestKeyDown,
    handleSelect: handleSuggestSelect,
    handleBlur: handleSuggestBlur,
    handleFocus: handleSuggestFocus,
  } = useSearchSuggestions({ onSelect: handleSuggestionSelect })

  const onAccountClick = () => {
    fireCsEvent({
      category: "Header",
      action: "Engagement",
      label: "Account button click"
    })
    if (!loggedIn) {
      openLoginModal()
    } else {
      history.push("/account")
    }
  }

  return (
    <>
      <ForgotPasswordModal isOpen={showForgot} onClose={() => setShowForgot(false)} />
      <HeaderOuter role="banner" $scrolled={scrolled}>
        {isLoginOpen && <LoginModal
          onClose={() => closeAllModals()} onForgotPassword={() => {
            closeAllModals()
            fireCsEvent({
              category: 'Header',
              action: 'Engagement',
              label: 'Forgot password clicked'
            })
            setShowForgot(true)
          }} />}
        {isSignupOpen && <SignupModal onClose={() => closeAllModals()} />}

        <HeaderInner $scrolled={scrolled}>
          <LogoLink to="/" rel="home"
            onClick={() => {
              fireCsEvent({
                category: 'Header',
                action: 'Navigation',
                label: 'Logoya basıldı'
              })
            }}>
            <LogoImg src="/static/favicon.png" alt="" />
            <LogoText>
              <FormattedMessage id="name"/>
            </LogoText>
          </LogoLink>

          <SearchArea>
            <SearchForm method="get" target="_top"
              action={process.env.REACT_APP_GA_BASE_URL + `/${getSearchPathPrefix(tenant)}`}
              onSubmit={ara}>
              <SearchBtn type="submit" aria-label="Ara">
                <BsSearch />
              </SearchBtn>
              <SearchInput type="search"
                name="q"
                ref={setAraInput}
                placeholder={intl.formatMessage({ id: "ara.cta", defaultMessage: "Gazetelerde arayın" })}
                onInput={handleSuggestInput}
                onKeyDown={handleSuggestKeyDown}
                onBlur={handleSuggestBlur}
                onFocus={handleSuggestFocus}
                autoComplete="off"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={suggestionsOpen}
                aria-controls="search-suggestions-listbox" />
            </SearchForm>
            <SuggestionsDropdown
              suggestions={headerSuggestions}
              isOpen={suggestionsOpen}
              activeIndex={activeIndex}
              onSelect={handleSuggestSelect}
            />
          </SearchArea>

          <HeaderActions>
            <HamburgerMenu
              loggedIn={loggedIn}
              user={user}
              openLoginModal={openLoginModal}
              openSignupModal={openSignupModal}
              closeAllModals={closeAllModals}
              onAccountClick={onAccountClick}
              windowWidth={windowSize.width ?? 1024}
            />
          </HeaderActions>
        </HeaderInner>

      </HeaderOuter>
    </>
  )
}
